import { useEffect, useState } from 'react';
import { Avatar, Button, Card, Col, Empty, message, Popconfirm, Row, Tag, Typography } from 'antd';
import { UsergroupAddOutlined, DisconnectOutlined, UserOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { fetchConnections, disconnectFromCompany, Connection } from '../api/network';

const { Title, Text } = Typography;

export default function Connections() {
  const { token } = useAuth();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetchConnections(token).then(data => { setConnections(data); setLoading(false); });
  }, [token]);

  async function disconnect(companyId: string, companyName: string) {
    if (!token) return;
    const ok = await disconnectFromCompany(token, companyId);
    if (ok) {
      setConnections(prev => prev.filter(c => c.company_id !== companyId));
      message.success(`Disconnected from ${companyName}`);
    } else {
      message.error('Failed to disconnect');
    }
  }

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>Loading connections...</div>;
  }

  if (connections.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3} style={{ marginBottom: 24 }}>My Connections</Title>
        <Empty
          image={<UsergroupAddOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description={
            <span>
              No connections yet.{' '}
              <a href="/">Browse the directory</a> to connect with businesses.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>My Connections</Title>
        <Text type="secondary">{connections.length} connections</Text>
      </div>

      <Row gutter={[16, 16]}>
        {connections.map((conn: Connection) => (
          <Col key={conn.id} xs={24} sm={12} lg={8}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {conn.logo_url
                    ? <Avatar size={40} src={conn.logo_url} />
                    : <Avatar size={40} icon={<UserOutlined />} style={{ background: '#722ed1', flexShrink: 0 }} />
                  }
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text strong>{conn.company_name}</Text>
                      {conn.is_verified && <CheckCircleFilled style={{ color: '#1677ff', fontSize: 13 }} />}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <Tag color="blue" style={{ fontSize: 11 }}>{conn.company_type.replace('_', ' ')}</Tag>
                      {conn.city && <Text type="secondary" style={{ fontSize: 12 }}>{conn.city}</Text>}
                    </div>
                    {conn.connected_at && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                        Connected {new Date(conn.connected_at).toLocaleDateString()}
                      </Text>
                    )}
                  </div>
                </div>
                <Popconfirm
                  title="Remove connection?"
                  onConfirm={() => disconnect(conn.company_id, conn.company_name)}
                  okText="Remove" cancelText="Cancel" okButtonProps={{ danger: true }}
                >
                  <Button size="small" icon={<DisconnectOutlined />} danger type="text" />
                </Popconfirm>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
